import { Controller } from '@hotwired/stimulus';

// Turns a textarea into the same CKEditor instance the post form uses.
// The editor is imported lazily so its bundle only loads on pages that need it.
export default class extends Controller {
  static values = {
    uploadUrl: String,
    imageTypes: Array,
    ctaBlock: Boolean,
    ctaLabel: String,
    ctaUrl: String,
    layoutBlocks: Boolean
  };

  async connect() {
    this.connected = true;

    const { default: Editor } = await import('~/controllers/post_create/ckeditor/editor');

    if (!this.connected) return;

    try {
      const editor = await Editor.create(this.element, this.editorConfig(Editor));

      // Turbo can tear the element down while the editor is still being built.
      // At that point disconnect() had nothing to destroy yet, so the instance
      // has to clean up after itself instead of being stored on a dead
      // controller and leaking with its detached DOM.
      if (this.connected) {
        this.editor = editor;
      } else {
        editor.destroy();
      }
    } catch (error) {
      console.error(error);
    }
  }

  disconnect() {
    this.connected = false;
    this.editor?.destroy();
    this.editor = null;
  }

  // The call to action and the layout blocks are a content page's tools, so
  // their buttons only join the toolbar where the page that mounted the editor
  // asked for them.
  editorConfig(Editor) {
    const config = {};

    if (this.hasUploadUrlValue) {
      config.simpleUpload = {
        uploadUrl: this.uploadUrlValue,
        withCredentials: true,
        headers: {
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
        }
      };
    }

    if (this.hasImageTypesValue) {
      config.image = {
        upload: {
          types: this.imageTypesValue
        },
        insert: {
          type: 'block',
        },
      };
    }

    const blockItems = [];

    if (this.ctaBlockValue) {
      blockItems.push('ctaBlock');
      config.ctaBlock = {
        label: this.ctaLabelValue,
        url: this.ctaUrlValue
      };
    }

    if (this.layoutBlocksValue) {
      blockItems.push('figureQuote', 'callout');
    }

    if (blockItems.length) {
      config.toolbar = {
        ...Editor.defaultConfig.toolbar,
        items: [...Editor.defaultConfig.toolbar.items, '|', ...blockItems]
      };
    }

    return config;
  }
}
